import { Link } from "@material-ui/core";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import { useEffect } from "react";
import {
  Activity,
  BarChart2,
  Bell,
  Book,
  BookOpen,
  CheckCircle,
  FileText,
  Folder,
  Globe,
  Image,
  Layers,
  List,
  MessageCircle,
  Users,
  Video
} from "react-feather";
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from "react-redux";
import { getDashboardSummary } from "src/redux/actions";

const Dashboard = () => {
  const dispatch = useDispatch();
  const summary = useSelector((state) => state?.mainReducer?.dashboardSummary);

  useEffect(() => {
    dispatch(getDashboardSummary());
  }, [dispatch]);

  const dashboardItems = [
    {
      label: "LANGUAGES",
      value: "courses",
      action: "Manage languages",
      href: "/app/languages",
      icon: <Globe size={40} color="#2196f3" />,
    },
    {
      label: "COURSES",
      value: "units",
      action: "Manage courses",
      href: "/app/courses",
      icon: <BookOpen size={40} color="#4caf50" />,
    },
    {
      label: "CHAPTERS",
      value: "chapters",
      action: "Manage chapters",
      href: "/app/chapters",
      icon: <Layers size={40} color="#ff9800" />,
    },
    {
      label: "LESSONS",
      value: "lessons",
      action: "Manage lessons",
      href: "/app/lessons",
      icon: <FileText size={40} color="#9c27b0" />,
    },
    {
      label: "VIDEOS",
      value: "videos",
      action: "Manage videos",
      href: "/app/4l-videos",
      icon: <Video size={40} color="#f44336" />,
    },
    {
      label: "4L categories",
      value: "videoCategories",
      action: "Manage videos categories",
      href: "/app/4l-categories",
      icon: <Folder size={40} color="#03a9f4" />,
    },
    {
      label: "BANNERS",
      value: "banners",
      action: "Manage banners",
      href: "/app/banners",
      icon: <Image size={40} color="#ff5722" />,
    },
    {
      label: "VOCABULARIES",
      value: "vocabularies",
      action: "Manage vocabularies",
      href: "/app/entertainmentVocabularies",
      icon: <Book size={40} color="#e91e63" />,
    },
    {
      label: "PRACTICE LIST",
      value: "exerciseMasters",
      action: "Manage practice list",
      href: "/app/practice-list",
      icon: <List size={40} color="#795548" />,
    },
    {
      label: "PRACTICE ITEM",
      value: "exerciseDetails",
      action: "Manage practice item",
      href: "/app/practice-item",
      icon: <CheckCircle size={40} color="#009688" />,
    },
    {
      label: "USERS",
      value: "users",
      action: "Manage users",
      href: "/app/customers",
      icon: <Users size={40} color="#673ab7" />,
    },
    {
      label: "FEEDBACK",
      value: "feedbacks",
      action: "Manage feedback",
      href: "/app/feedbacks",
      icon: <MessageCircle size={40} color="#607d8b" />,
    },
    // {
    //   label: "SERVICE LOGS",
    //   value: "serviceLogs",
    //   action: "Manage service logs",
    //   href: "/app/serviceLogs",
    //   icon: <Activity size={40} color="#ff5722" />,
    // },
    // {
    //   label: "REPORTS",
    //   value: "reports",
    //   action: "Manage reports",
    //   href: "/app/reports",
    //   icon: <BarChart2 size={40} color="#3f51b5" />,
    // },
    {
      label: "NOTIFICATION",
      value: "notifications",
      action: "Manage notifications",
      href: "/app/notifications",
      icon: <Bell size={40} color="#ffc107" />,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Dashboard | Langistan</title>
      </Helmet>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          DASHBOARD
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Dashboard gives a quick overview of platform content and lets manage courses, lessons, chapters, videos, users, and more.
        </Typography>

        {/* Responsive Grid */}
        <Grid container spacing={3}>
          {dashboardItems?.map((item, index) => {
            const countData = summary[item?.value]
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Link
                  href={item?.href}
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 3,
                      boxShadow: 3,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: 8,
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: "center", p: 3 }}>
                      {/* Icon */}
                      <Box
                        sx={{
                          mb: 2,
                          color: "primary.main",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {item?.icon}
                      </Box>

                      {/* Total Count Highlight */}
                      <Typography variant="h4" fontWeight="bold" gutterBottom>
                        {countData?.total ?? 0}
                      </Typography>

                      {/* Active & Inactive stats */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-around",
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ color: "success.main" }}
                          >
                            Active
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {countData?.active ?? 0}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ color: "error.main" }}
                          >
                            InActive
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {countData?.inActive ?? 0}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Label */}
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          color: "#1976d2",
                        }}
                        sx={{ mb: 1 }}
                      >
                        {item?.label}
                      </Typography>

                      {/* Action link */}
                      {/* <Typography
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "#1976d2",
                        textDecoration: "none",
                      }}
                    >
                      {item?.action}
                    </Typography> */}
                    </CardContent>
                  </Card>
                </Link>
              </Grid>
            )
          })}
        </Grid>

      </Box >
    </>
  );
};

export default Dashboard;
